import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { ApiService } from '@core/services/api';
import { TabService } from '@core/services/tab.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { exportFileBaseName } from '@core/utils/export-file-name';
import { TranslateService } from '@ngx-translate/core';
import type { FormProcessButton } from '@shared/components/kl-form-container/kl-form-container';
import type { ActionDto, GridActionContext, GridColumn } from '@shared/components/kl-grid/grid.models';
import type { ColumnFilterState } from '@shared/components/kl-grid/kl-column-filter/kl-column-filter';
import { KlGridColumnSettingsDrawer } from '@shared/components/kl-grid/kl-grid-column-settings-drawer/kl-grid-column-settings-drawer';
import { VIRTUAL_SCROLL_ROW_THRESHOLD } from '@shared/components/kl-grid/kl-grid-container/kl-grid-container';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import type { TableColumnReorderEvent, TablePageEvent } from 'primeng/table';
import { formatKlDateValue, getUserDateFormat, momentFormatToPrimeNg } from '../../utils/kl-date-format';
import { columnTrackKey as computeColumnTrackKey } from './kl-grid-form-column-track.util';
import { hasPastableCell, parsePastedText, setNestedValue, writePastedCells } from './kl-grid-form-paste.util';
import type {
  KlGridFormLoadingProgress,
  KlGridFormPastedRow,
  KlGridFormPasteAnchor,
  KlGridFormPasteEvent,
} from './kl-grid-form.models';

export interface KlGridFormCellButtonContext<T extends object = Record<string, unknown>> {
  row: T;
  column: KlGridFormColumn<T>;
  rowIndex: number;
  event: Event;
}

export interface KlGridFormCellDisabledContext<T extends object = Record<string, unknown>> {
  row: T;
  column: KlGridFormColumn<T>;
  rowIndex: number;
}

export interface KlGridFormButtonConfig<T extends object = Record<string, unknown>> {
  /** Icon CSS classes (e.g. `ph ph-list`). */
  icon?: string;
  /** Visible label i18n key; when set on `action-button` columns, renders icon + text. */
  label?: string;
  /** Row action; `rowIndex` is the source index in {@link KlGridForm.rows}. */
  method?: (ctx: KlGridFormCellButtonContext<T>) => void;
  /** Per-row disable predicate (e.g. row not yet eligible for the action); enabled when omitted. */
  disabled?: (ctx: KlGridFormCellDisabledContext<T>) => boolean;
}

type KlGridFormColumnType =
  | 'text'
  | 'number'
  | 'decimal'
  | 'index'
  | 'select'
  | 'checkbox'
  | 'picker'
  | 'button'
  | 'action-button'
  | 'password';

interface KlGridFormColumnBase<T extends object> {
  header: string;
  binding: string;
  width?: number | string;
  /** Column visibility when grid design preferences are enabled (default visible). */
  visible?: boolean;
  /** Button cell config for `type: 'button'` / `type: 'action-button'`. */
  button?: KlGridFormButtonConfig<T>;
  isReadOnly?: boolean;
  /** Per-row read-only on top of `isReadOnly`: the cell is read-only for rows where this returns true. */
  isReadOnlyForRow?: (row: T) => boolean;
  /** Static options, or a per-row resolver returning the row's own (stable) array or undefined. */
  options?: unknown[] | ((row: T) => unknown[] | undefined);
  /** Property name displayed in dropdown label. */
  optionLabel?: string;
  /** When set, `p-select` binds this property (primitive) instead of the whole option object. */
  optionValue?: string;
  /** When true, `optionLabel` values are ngx-translate keys (dropdown + read-only display). */
  translateOptionLabels?: boolean;
  /** When true, cell values are treated as i18n keys and translated (applies to text/select columns). */
  translate?: boolean;
  /** Date display/edit when `'date'` or `'date-time'` (same as {@link GridColumn.format}). */
  format?: GridColumn['format'];
  /** Static per-column limit, or a per-row resolver (e.g. capping a value cell to another cell's own length). */
  maxLength?: number | ((row: T) => number);
  min?: number;
  max?: number;
  /**
   * Fixed fractional digits for **read-only / output** display (`pTemplate="output"`, read-only cells). Default **0**; set e.g. **4** for `f4` amounts.
   */
  displayFractionDigits?: number;
  /** Column header sort (kl-column-filter); default true when {@link KlGridForm.enableColumnSort} is on. */
  sortable?: boolean;
  /** Column value checklist filter; default true when {@link KlGridForm.enableColumnFilter} is on. */
  filterable?: boolean;
  /**
   * When `type` is `picker`, dot-path on the row used for display (e.g. `bank_account.code`).
   * Defaults to `{binding}.code` when unset.
   */
  pickerDisplayPath?: string;
  /** Value stored at {@link binding} when the user clears the picker (default `{}`). */
  pickerClearValue?: unknown;
  /** Shallow-merged onto the row after clearing the binding (optional). */
  pickerClearExtraPatch?: Record<string, unknown>;
  /** Stable `@for` identity when {@link binding} is reused across columns. Defaults to header + index. */
  trackKey?: string;
  /** When `type` is `picker`, also renders an editable text input (typing/paste) alongside the lookup button. */
  typablePicker?: boolean;
}

interface KlGridFormDecimalColumn<T extends object> extends KlGridFormColumnBase<T> {
  type: 'decimal';
  /**
   * Fraction digits for **`p-inputNumber`** — required so the decimal separator (`,`/`.`) key isn't
   * silently swallowed by PrimeNG (which drops it whenever `maxFractionDigits` is 0). Use `type: 'number'`
   * instead for integer columns — this variant exists so a fractional column can't forget this config.
   */
  minFractionDigits: number;
  maxFractionDigits: number;
}

interface KlGridFormOtherColumn<T extends object> extends KlGridFormColumnBase<T> {
  type?: Exclude<KlGridFormColumnType, 'decimal'>;
  minFractionDigits?: number;
  maxFractionDigits?: number;
}

export type KlGridFormColumn<T extends object = Record<string, unknown>> =
  | KlGridFormDecimalColumn<T>
  | KlGridFormOtherColumn<T>;

/**
 * Toolbar flags for the grid toolbar (search, export, import, …).
 * All flags default off except what `showToolbar` / add-row already control.
 */
export interface KlGridFormToolbarOptions {
  /** When false, hides the entire toolbar row. */
  show?: boolean;
  /** Collapsible client-side filter over visible columns. */
  search?: boolean;
  /** Built-in Excel download (translated headers, current row model). */
  export?: boolean;
  /** Base file name without extension (default `export`). */
  exportFileName?: string;
  /** Import → Excel menu entry; opens file picker and emits {@link KlGridForm.importFile}. */
  import?: boolean;
  /** Expand grid block to viewport (class + {@link KlGridForm.fullscreenChange}). */
  fullscreen?: boolean;
  /** Toolbar button; emits {@link KlGridForm.bulkDelete}. */
  bulkDelete?: boolean;
}

/** Cached formatter per fraction-digit count; read-only numeric cells are formatted on every change detection. */
const numberFormatters = new Map<number, Intl.NumberFormat>();

function getNumberFormatter(fractionDigits: number): Intl.NumberFormat {
  let formatter = numberFormatters.get(fractionDigits);
  if (!formatter) {
    formatter = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    });
    numberFormatters.set(fractionDigits, formatter);
  }
  return formatter;
}

@Component({
  selector: 'kl-grid-form',
  standalone: false,
  templateUrl: './kl-grid-form.html',
  styleUrl: './kl-grid-form.scss',
})
export class KlGridForm<T extends object = Record<string, unknown>> {
  private readonly translate = inject(TranslateService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly api = inject(ApiService);
  private readonly userEnv = inject(UserEnvService);
  private readonly tabService = inject(TabService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly dateCellCache = new WeakMap<T, Map<string, { raw: unknown; date: Date | null }>>();

  // Native Fullscreen API (not just a CSS `position: fixed` class) so the expanded grid escapes
  // `.layout-content`'s stacking context (position: relative + z-index: 0) instead of being trapped
  // under it - a plain fixed+z-index element there can never paint above page chrome like the
  // breadcrumb bar, no matter how high its z-index goes.
  constructor() {
    const syncFullscreen = (): void => {
      const active =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement;
      const isActive = active === this.host.nativeElement;
      this.isFullscreen.set(isActive);
      this.fullscreenChange.emit(isActive);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      if (
        document.fullscreenElement === this.host.nativeElement ||
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ===
          this.host.nativeElement
      ) {
        void this.exitNativeFullscreen();
      }
    });

    // Capture phase: p-select stops Tab on its own element, so the event never bubbles up to the cell.
    const onKeydown = (event: KeyboardEvent): void => {
      this.onCellEditorTab(event);
      this.onCellEnter(event);
    };
    this.host.nativeElement.addEventListener('keydown', onKeydown, true);
    this.destroyRef.onDestroy(() => this.host.nativeElement.removeEventListener('keydown', onKeydown, true));

    // Capture phase: p-inputNumber handles paste on its own input and would write a broken value first.
    const onPaste = (event: ClipboardEvent): void => this.onCellPaste(event);
    this.host.nativeElement.addEventListener('paste', onPaste, true);
    this.destroyRef.onDestroy(() => this.host.nativeElement.removeEventListener('paste', onPaste, true));
  }

  readonly columns = input<KlGridFormColumn<T>[]>([]);
  /** Design key (e.g. `grdCashierAuthGroupPermissionLine`). */
  readonly gridName = input('');
  /** Design module (e.g. `CASHIER_AUTH_GROUP_PERMISSION_FORM`). */
  readonly moduleName = input('');
  /** Design parent module (default `APPLICATION_PARAMS`). */
  readonly parentModuleName = input('APPLICATION_PARAMS');
  readonly rows = model<T[]>([]);
  readonly isReadOnly = input(false);
  readonly showToolbar = input(true);
  readonly showAddRow = input(true);
  /**
   * When set, toolbar "add" uses this factory instead of blank cells derived from {@link columns}
   * (e.g. `rec_status`, UUIDs, domain defaults).
   */
  readonly addRowFactory = input<(() => T) | null>(null);
  /**
   * When true, toolbar "add" emits {@link KlGridForm.addRowClick} instead of appending a blank row
   * (e.g. list screens that open a dialog on add).
   */
  readonly interceptAddRow = input(false);
  /** When set, `addRow()` opens the PrimeNG cell editor on this column `binding` for the new row; no-op unless set. */
  readonly focusColumnOnAdd = input<string | null>(null);
  readonly scrollHeight = input('min(360px, 50vh)');
  /** Opt-in DOM windowing past {@link VIRTUAL_SCROLL_ROW_THRESHOLD} visible rows; not for `showPaginator` / `focusColumnOnAdd` grids. */
  readonly virtualScroll = input(false);
  /** Fixed row height in px for {@link virtualScroll} (must match the CSS row height). */
  readonly virtualScrollItemSize = input(30);
  /** Show built-in p-table paginator (server-side / lazy). */
  readonly showPaginator = input(false);
  readonly paginatorPageSize = input(15);
  /** Zero-based row offset of the current page (same as API paging.offset). */
  readonly pageFirst = input(0);
  /** Server-side total row count. */
  readonly totalRecords = input(0);
  /** Grid loading — logo loader in an empty-grid panel on first load, overlay when rows already visible. */
  readonly loading = input(false);
  readonly loadingText = input('loading');
  /** Optional `loaded / total` text and bar inside the loading state; nothing extra is shown when null. */
  readonly loadingProgress = input<KlGridFormLoadingProgress | null>(null);
  /** Extra controls in the toolbar row (after built-in actions). */
  readonly toolbarTemplate = input<TemplateRef<unknown> | null>(null);
  /** Toolbar toggles (search, export, import, …). */
  readonly toolbarOptions = input<Partial<KlGridFormToolbarOptions> | null>(null);
  /** Grid-scoped process menu. */
  readonly processButtons = input<FormProcessButton[]>([]);
  /** Custom toolbar buttons (same pattern as `kl-grid-container`). */
  readonly customToolbar = input<ActionDto[]>([]);
  /** When false, hides {@link customToolbar} buttons (parent can still pass an empty array). */
  readonly customToolbarVisible = input(true);
  /** Client-side column header sort via `kl-column-filter`. */
  readonly enableColumnSort = input(true);
  /** Distinct-value column filter via `kl-column-filter` (flat `binding` paths work best). */
  readonly enableColumnFilter = input(true);
  /** Drag column headers to reorder; updates display order and emits {@link KlGridForm.columnOrderChange}. */
  readonly reordering = input(true);
  /** Drag row handle to reorder rows; writes order back to `rows`. Clears column sort when used. */
  readonly rowReordering = input(false);
  /** Enables row delete action from context menu. */
  readonly allowDelete = input(true);
  /** Lets the last remaining row be deleted too. */
  readonly allowRemoveLastRow = input(true);
  /** Extra row-level actions appended to the context menu (below the built-in "delete", separated). */
  readonly contextMenuActions = input<ActionDto[]>([]);
  /**
   * When true, a `type: 'checkbox'` column with `binding: 'selected'` gets a header checkbox
   * that toggles selection on all loaded rows.
   */
  readonly selectAllCheckbox = input(false);
  /** Alternating row background (same pattern as `kl-grid` body rows). */
  readonly stripedRows = input(true);
  /**
   * When set, highlights the body row whose source index in {@link KlGridForm.rows} matches this value
   * (same index space as {@link KlGridForm.cellChanged}'s `rowIndex`).
   */
  readonly selectedRowIndex = input<number | null>(null);
  /** When true, editable-cell clicks (and Enter/Tab navigation, which clicks the next `td`; arrow keys don't) also fire {@link KlGridForm.rowActivate} / {@link KlGridForm.rowDblClick}; open editors are still ignored. */
  readonly activateOnEditableCell = input(false);
  /**
   * When set, shows `[loading]` on the matching `type: 'button'` cell (`rowIndex` is the **source** index in `rows()`).
   */
  readonly cellButtonPending = input<{ rowIndex: number; binding: string } | null>(null);
  /** When set, rows for which this returns `true` get a highlighted background (e.g. manually-edited rows). */
  readonly rowHighlight = input<((row: T) => boolean) | null>(null);
  /** Second, independent highlight predicate (distinct color) — e.g. deposit-type sale lines. */
  readonly rowHighlightSecondary = input<((row: T) => boolean) | null>(null);
  /** Optional extra class names for a body row (e.g. price-change promo/activation tints). */
  readonly rowClass = input<((row: T) => string) | null>(null);
  /** When set, rows for which this returns `false` can't be deleted (no "delete" in their context menu). */
  readonly rowDeletable = input<((row: T) => boolean) | null>(null);

  /** Emitted when the user changes page via the table paginator. */
  readonly tablePageChange = output<{ first: number; rows: number }>();
  readonly importFile = output<File>();
  readonly fullscreenChange = output<boolean>();
  readonly bulkDelete = output<void>();
  /** Column `binding` values in header order after a drag reorder (for persisting layout). */
  readonly columnOrderChange = output<string[]>();
  /** Emitted when a custom toolbar button is clicked (parent handles action). */
  readonly customToolbarAction = output<{ action: ActionDto; row: T | null }>();

  readonly rowContextMenuRef = viewChild<ContextMenu>('rowContextMenu');
  readonly columnSettingsDrawer = viewChild<KlGridColumnSettingsDrawer>('columnSettingsDrawer');

  /** Built when opening the row context menu (delete row). */
  rowContextMenuModel: MenuItem[] = [];

  readonly sidebarVisible = signal(false);
  readonly effectiveColumns = signal<KlGridFormColumn<T>[]>([]);

  readonly columnPreferencesEnabled = computed(() => !!this.gridName().trim() && !!this.moduleName().trim());

  /** First load with no rows yet — full loader panel. */
  readonly showInitialLoader = computed(() => this.loading() && this.rows().length === 0);

  /** Refresh / pagination while rows are visible — overlay on top of the table. */
  readonly showLoadingOverlay = computed(() => this.loading() && this.rows().length > 0);

  readonly pageReportTemplate = computed(() => {
    const label = this.translate.instant('page');
    return `${label} {currentPage} / {totalPages}`;
  });

  readonly gridPaginatorReportParams = computed(() => {
    const total = this.totalRecords();
    const offset = this.pageFirst();
    const size = this.paginatorPageSize();
    const first = total > 0 ? offset + 1 : 0;
    const last = total > 0 ? Math.min(offset + size, total) : 0;
    return { first, last, total };
  });

  readonly tableLeadColSpan = computed(() => {
    let span = this.displayedDataColumns().length;
    if (this.rowReorderActive()) {
      span += 1;
    }
    if (this.columnPreferencesEnabled()) {
      span += 1;
    }
    return span;
  });

  private lastDesignFetchKey = '';
  /** Anchor cell of the last multi-cell paste until the user types or commits another cell; see {@link onCellCommit}. */
  private pasteAnchor: KlGridFormPasteAnchor | null = null;

  readonly searchPanelOpen = signal(false);
  readonly searchQuery = signal('');
  readonly isFullscreen = signal(false);

  /** PrimeNG mutates this array in place for row reorder — keep in sync via `effect`. */
  readonly orderedViewRows = signal<T[]>([]);

  /** Below the threshold the table keeps its auto-width layout, so short documents look unchanged. */
  readonly effectiveVirtualScroll = computed(
    () => this.virtualScroll() && this.orderedViewRows().length > VIRTUAL_SCROLL_ROW_THRESHOLD
  );

  /** Rows kept rendered beyond the viewport; PrimeNG's default (half a viewport) shows blank rows on a fast scroll. */
  readonly virtualScrollOptions = { numToleratedItems: 12 };

  // Scroller skips init while an ancestor is display:none; re-check once the grid gets a size.
  private readonly virtualScrollVisibilitySync = effect((onCleanup) => {
    if (!this.effectiveVirtualScroll()) {
      return;
    }
    let hadSize = false;
    const observer = new ResizeObserver(([entry]) => {
      const hasSize = entry.contentRect.height > 0;
      if (hasSize && !hadSize) {
        this.cdr.markForCheck();
      }
      hadSize = hasSize;
    });
    observer.observe(this.host.nativeElement);
    onCleanup(() => observer.disconnect());
  });

  /**
   * PrimeNG column reorder mutates this array in place; synced from {@link KlGridForm.columns} and
   * refreshed via {@link KlGridForm.onColReorder}.
   */
  readonly dataColumnMeta = signal<{ field: string }[]>([]);

  readonly columnFilterStates = signal(new Map<string, ColumnFilterState>());
  readonly sortBinding = signal<string | null>(null);
  readonly sortDesc = signal(false);

  private readonly effectiveColumnsSync = effect(() => {
    const cols = this.columns();
    untracked(() => {
      const prev = this.effectiveColumns();
      // Parent often re-emits column defs (e.g. currency options). Do not wipe loaded GRID_DESIGN.
      this.effectiveColumns.set(
        prev.length === 0 ? cols.map((c) => ({ ...c })) : this.mergeColumnDefsPreservingDesign(cols, prev)
      );
    });
  });

  private readonly columnMetaSync = effect(() => {
    const cols = this.effectiveColumns().filter((c) => c.visible !== false);
    const bindings = cols.map((c) => c.binding);
    untracked(() => {
      const cur = this.dataColumnMeta().map((m) => m.field);
      if (cur.length === bindings.length && cur.every((k, i) => k === bindings[i])) {
        return;
      }
      this.dataColumnMeta.set(bindings.map((b) => ({ field: b })));
    });
  });

  private readonly defaultDesignLoad = effect(() => {
    if (!this.columnPreferencesEnabled()) {
      return;
    }
    const mod = this.moduleName().trim();
    const key = this.gridName().trim();
    const parent = this.parentModuleName().trim() || 'APPLICATION_PARAMS';
    const companyId = this.userEnv.env()?.company?.id;
    const cols = this.columns();
    if (!mod || !key || companyId == null || cols.length === 0) {
      return;
    }
    const fetchKey = `${parent}|${mod}|${key}|${companyId}`;
    if (fetchKey === this.lastDesignFetchKey) {
      return;
    }
    this.lastDesignFetchKey = fetchKey;
    untracked(() => this.loadDefaultGridDesign(parent, mod, key, Number(companyId)));
  });

  /** Data columns in current header order (after optional column drag). */
  readonly displayedDataColumns = computed(() => {
    this.dataColumnMeta();
    const cols = this.effectiveColumns();
    const map = new Map(cols.map((c) => [c.binding, c] as const));
    return this.dataColumnMeta()
      .map((m) => map.get(m.field))
      .filter((c): c is KlGridFormColumn<T> => c != null && c.visible !== false);
  });

  /** Min table width so wide column sets scroll horizontally inside the scrollable host. */
  readonly tableMinWidthStyle = computed(() => {
    let total = 0;
    if (this.columnPreferencesEnabled()) {
      total += KlGridForm.SETTINGS_COL_WIDTH_PX;
    }
    if (this.rowReorderActive()) {
      total += KlGridForm.REORDER_COL_WIDTH_PX;
    }
    for (const col of this.displayedDataColumns()) {
      total += this.resolveColumnMinWidthPx(col.width);
    }
    // `max(..., 100%)` keeps this as a scroll-triggering floor for wide column sets while
    // still letting the table stretch to fill the host when columns don't fill it on their own
    // (a bare px value would otherwise win over the `.p-datatable-table` CSS `min-width: 100%`
    // rule, since this is applied as an inline style).
    return { 'min-width': `max(${Math.max(total, 1)}px, 100%)` };
  });

  private static readonly DEFAULT_COLUMN_MIN_WIDTH_PX = 120;
  private static readonly SETTINGS_COL_WIDTH_PX = 30;
  private static readonly REORDER_COL_WIDTH_PX = 36;
  /** Body-cell `td` that opens a PrimeNG cell editor on click; only editable cells render it. */
  private static readonly editableCellSelector = 'td[data-p-editable-column]';
  /** Text / number / decimal editor cells; Enter moves them down a row. */
  private static readonly enterNavigableCellSelector = 'td[data-p-editable-column][data-enter-nav]';

  private readonly viewSync = effect(() => {
    this.rows();
    this.searchQuery();
    this.columnFilterStates();
    this.sortBinding();
    this.sortDesc();
    this.effectiveColumns();
    this.enableColumnSort();
    this.enableColumnFilter();
    untracked(() => {
      this.orderedViewRows.set(this.buildViewRows());
    });
  });

  readonly effectiveToolbar = computed(() => {
    const o = this.toolbarOptions();
    return {
      show: o?.show !== false,
      search: o?.search === true,
      export: o?.export === true,
      exportFileName: o?.exportFileName?.trim() || 'export',
      import: o?.import === true,
      fullscreen: o?.fullscreen !== false,
      bulkDelete: o?.bulkDelete === true,
    };
  });

  /** Bulk delete toolbar action shows only when rows are checked. */
  readonly hasBulkDeleteSelection = computed(() => this.rows().some((row) => this.getNested(row, 'selected') === true));

  readonly showBulkDeleteAction = computed(() => this.effectiveToolbar().bulkDelete && this.hasBulkDeleteSelection());

  readonly showToolbarArea = computed(() => {
    if (this.columnPreferencesEnabled()) {
      return true;
    }
    if (!this.effectiveToolbar().show) {
      return false;
    }
    if (!this.showToolbar()) {
      return false;
    }
    const readOnly = this.isReadOnly();
    const t = this.effectiveToolbar();
    const hasAdd = !readOnly && this.showAddRow();
    const hasTemplate = this.toolbarTemplate() != null;
    const hasBuiltIn =
      t.search ||
      t.export ||
      t.import ||
      t.fullscreen ||
      t.bulkDelete ||
      this.processButtons().some((p) => p.show !== false);
    if (readOnly) {
      return hasTemplate || hasBuiltIn;
    }
    return hasAdd || hasTemplate || hasBuiltIn;
  });

  readonly addRowClick = output<void>();
  readonly rowAdded = output<T>();
  readonly rowRemoved = output<{ row: T; index: number }>();
  readonly cellChanged = output<{
    row: T;
    column: KlGridFormColumn<T>;
    rowIndex: number;
  }>();
  readonly pasteCells = output<KlGridFormPasteEvent>();
  /** `type: 'picker'` column with {@link KlGridFormColumn.typablePicker}: user typed/committed raw text. */
  readonly pickerTextInput = output<{
    row: T;
    column: KlGridFormColumn<T>;
    value: string;
    rowIndex: number;
  }>();
  /** `type: 'button'` cell clicked; `rowIndex` is the **source** index in `rows()`. */
  readonly cellButtonClick = output<{
    row: T;
    column: KlGridFormColumn<T>;
    rowIndex: number;
  }>();
  /** Fired when the user activates a row by clicking outside cell editors (source row index into `rows()`). */
  readonly rowActivate = output<number>();
  /** Fired when the user double-clicks a row outside cell editors (source row index into `rows()`). */
  readonly rowDblClick = output<number>();
  /** User clicked the lookup control on a `type: 'picker'` column (parent opens a dialog). */
  readonly pickerRequest = output<{ rowIndex: number; column: KlGridFormColumn<T> }>();

  toggleSearchPanel(): void {
    this.searchPanelOpen.update((v) => !v);
  }

  /** Header “select all” for {@link KlGridForm.selectAllCheckbox} + `selected` checkbox column. */
  readonly allRowsSelected = computed(() => {
    const r = this.rows();
    if (!r.length) {
      return false;
    }
    return r.every((row) => !!this.getNested(row, 'selected'));
  });

  isRowSelectionColumn(col: KlGridFormColumn<T>): boolean {
    return this.selectAllCheckbox() && col.type === 'checkbox' && col.binding === 'selected';
  }

  columnTrackKey(col: KlGridFormColumn<T>, index: number): string {
    return computeColumnTrackKey(col, index);
  }

  toggleSelectAll(checked: boolean): void {
    this.rows.set(this.rows().map((row) => ({ ...row, selected: checked })));
  }

  toggleFullscreen(): void {
    if (this.isFullscreen()) {
      void this.exitNativeFullscreen();
      return;
    }
    const el = this.host.nativeElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
    }
  }

  private exitNativeFullscreen(): Promise<void> | void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (doc.webkitExitFullscreen) {
      return doc.webkitExitFullscreen();
    }
  }

  exportXlsx(): void {
    const rows = this.rows();
    if (!rows.length) {
      return;
    }
    const cols = this.displayedDataColumns();
    const headers = cols.map((c) => this.translate.instant(c.header));
    const data = rows.map((row, rowIndex) => {
      const obj: Record<string, string | number | boolean> = {};
      cols.forEach((col, ci) => {
        obj[headers[ci]] = this.exportCellValue(row, col, rowIndex);
      });
      return obj;
    });
    const fractionDigits = cols.map((col) => this.exportFractionDigits(col));
    const name = `${exportFileBaseName(this.translate, this.tabService, this.effectiveToolbar().exportFileName)}.xlsx`;
    Promise.all([import('xlsx-js-style'), import('../../utils/kl-xlsx-sheet')])
      .then(([mod, { formatXlsxSheet }]) => {
        // CJS package; production build can nest exports under `default`.
        const XLSX = mod.default ?? mod;
        const ws = XLSX.utils.json_to_sheet(data);
        formatXlsxSheet(ws, fractionDigits);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        XLSX.writeFile(wb, name);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  readonly processMenuItems = computed((): MenuItem[] => {
    const t = (key: string) => this.translate.instant(key);
    const items: MenuItem[] = [];
    for (const p of this.processButtons()) {
      if (p.show === false) {
        continue;
      }
      if (p.children?.length) {
        items.push({
          label: t(p.slug),
          icon: this.processMenuIcon(p.icon),
          items: p.children.map((c) => ({
            id: c.slug,
            label: t(c.slug),
            disabled: this.isProcessItemDisabled(c.disabled),
            command: () => {
              if (this.isProcessItemDisabled(c.disabled)) {
                return;
              }
              c.method();
            },
          })),
        });
      } else {
        items.push({
          id: p.slug,
          label: t(p.slug),
          icon: this.processMenuIcon(p.icon),
          disabled: this.isProcessItemDisabled(p.disabled),
          command: () => {
            if (this.isProcessItemDisabled(p.disabled)) {
              return;
            }
            p.method();
          },
        });
      }
    }
    return items;
  });

  private isProcessItemDisabled(disabled: boolean | (() => boolean) | undefined): boolean {
    if (disabled == null) {
      return false;
    }
    return typeof disabled === 'function' ? disabled() : !!disabled;
  }

  processMenuIcon(icon: string | undefined): string | undefined {
    if (!icon) {
      return undefined;
    }
    if (icon.startsWith('ph ')) {
      return icon;
    }
    return undefined;
  }

  onBulkDeleteClick(): void {
    this.bulkDelete.emit();
  }

  /** Clicks on editors / actions do not change row selection. */
  private static readonly editorIgnoreSelector =
    'input,select,textarea,button,a,.p-inputnumber,.p-inputtext,.p-select,.p-checkbox,[role=combobox],.kl-grid-form-row-handle';
  private static readonly rowActivateIgnoreSelector = `${KlGridForm.editorIgnoreSelector},.p-editable-column`;

  onTablePage(event: TablePageEvent): void {
    this.tablePageChange.emit({ first: event.first ?? 0, rows: event.rows ?? this.paginatorPageSize() });
  }

  onBodyRowClick(event: MouseEvent, displayIndex: number): void {
    if (this.isRowActivateTarget(event)) {
      this.rowActivate.emit(this.resolveSourceRowIndex(displayIndex));
    }
  }

  onBodyRowDblClick(event: MouseEvent, displayIndex: number): void {
    if (this.isRowActivateTarget(event)) {
      this.rowDblClick.emit(this.resolveSourceRowIndex(displayIndex));
    }
  }

  private isRowActivateTarget(event: MouseEvent): boolean {
    const el = event.target;
    const ignoreSelector = this.activateOnEditableCell()
      ? KlGridForm.editorIgnoreSelector
      : KlGridForm.rowActivateIgnoreSelector;
    return el instanceof Element && !el.closest(ignoreSelector);
  }

  onCellButtonClick(row: T, col: KlGridFormColumn<T>, displayIndex: number, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    const rowIndex = this.resolveSourceRowIndex(displayIndex);
    const ctx: KlGridFormCellButtonContext<T> = { row, column: col, rowIndex, event };
    col.button?.method?.(ctx);
    this.cellButtonClick.emit({
      row,
      column: col,
      rowIndex,
    });
  }

  isSourceRowSelected(displayIndex: number): boolean {
    const sel = this.selectedRowIndex();
    if (sel == null) {
      return false;
    }
    return this.resolveSourceRowIndex(displayIndex) === sel;
  }

  onBodyRowContextMenu(event: MouseEvent, displayIndex: number): void {
    const row = this.orderedViewRows()[displayIndex];
    const customActions = this.contextMenuActions().filter(
      (action) => !action.isRowVisible || action.isRowVisible(row)
    );
    const canDelete =
      this.allowDelete() && !this.isReadOnly() && this.canRemoveRow() && (this.rowDeletable()?.(row) ?? true);
    if (!canDelete && customActions.length === 0) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    const items: MenuItem[] = [];
    if (canDelete) {
      items.push({
        label: this.translate.instant('delete'),
        icon: 'ph ph-trash',
        command: () => this.removeRowAtDisplayIndex(displayIndex),
      });
    }
    if (canDelete && customActions.length > 0) {
      items.push({ separator: true });
    }
    for (const action of customActions) {
      items.push({
        label: this.translate.instant(action.slug),
        icon: action.icon,
        command: () => action.method?.({ item: row ?? {} }),
      });
    }
    this.rowContextMenuModel = items;
    setTimeout(() => this.rowContextMenuRef()?.show(event), 0);
  }

  removeRowAtDisplayIndex(displayIndex: number): void {
    const visible = this.orderedViewRows();
    const row = visible[displayIndex];
    if (!row) {
      return;
    }
    const idx = this.rows().indexOf(row);
    if (idx >= 0) {
      this.removeRow(idx);
    }
  }

  onColReorder(_event: TableColumnReorderEvent): void {
    if (!this.columnReorderActive()) {
      return;
    }
    const next = [...this.dataColumnMeta()];
    this.pinSelectionColumnFirst(next);
    this.dataColumnMeta.set(next);
    this.columnOrderChange.emit(next.map((m) => m.field));
  }

  /** Keep header “select all” column fixed at the start if column reorder runs. */
  private pinSelectionColumnFirst(meta: { field: string }[]): void {
    const sel = this.effectiveColumns().find((c) => this.isRowSelectionColumn(c));
    if (!sel) {
      return;
    }
    const idx = meta.findIndex((m) => m.field === sel.binding);
    if (idx > 0) {
      const [item] = meta.splice(idx, 1);
      meta.unshift(item);
    }
  }

  onRowReorder(): void {
    this.sortBinding.set(null);
    this.sortDesc.set(false);
    const orderedVisible = this.orderedViewRows();
    const merged = this.mergeVisibleRowOrderIntoRows(orderedVisible);
    this.rows.set(merged);
  }

  onToolbarAddRowClick(): void {
    if (this.interceptAddRow()) {
      this.addRowClick.emit();
      return;
    }
    this.addRow();
  }

  addRow(): void {
    this.focusNewRowColumn(this.appendRow());
  }

  /** Appends a blank (or {@link addRowFactory}) row, emits {@link rowAdded} and returns the new row. */
  private appendRow(): T {
    const row = this.createBlankRow();
    this.rows.set([...this.rows(), row]);
    this.rowAdded.emit(row);
    return row;
  }

  /** One row at a time, so a factory that numbers rows from the current row count sees each new row. */
  private appendRows(count: number): T[] {
    return Array.from({ length: count }, () => this.appendRow());
  }

  private createBlankRow(): T {
    const factory = this.addRowFactory();
    if (factory) {
      return factory();
    }
    // No factory: the grid cannot build a real T, so it starts empty and the columns fill it below.
    const blank = {} as T;
    this.columns().forEach((col) => {
      if (col.type === 'number' || col.type === 'decimal') {
        this.setNested(blank, col.binding, col.min ?? 0);
      } else if (col.type === 'checkbox') {
        this.setNested(blank, col.binding, false);
      } else {
        this.setNested(blank, col.binding, null);
      }
    });
    return blank;
  }

  /** See {@link KlGridForm.focusColumnOnAdd}; row isn't in the DOM yet this tick, so the click waits for the next one. */
  private focusNewRowColumn(row: T): void {
    const binding = this.focusColumnOnAdd();
    if (!binding) {
      return;
    }
    setTimeout(() => {
      const displayIndex = this.orderedViewRows().indexOf(row);
      if (displayIndex < 0) {
        return;
      }
      const nodeList: NodeListOf<HTMLElement> = this.host.nativeElement.querySelectorAll('kl-grid-form-body-cell');
      const cellHosts: HTMLElement[] = [];
      nodeList.forEach((el) => {
        if (el.getAttribute('data-binding') === binding) {
          cellHosts.push(el);
        }
      });
      const targetCell = cellHosts[displayIndex]?.querySelector('td');
      targetCell?.click();
    }, 0);
  }

  /** Tab / Shift+Tab in an open cell editor: commits the cell, then opens the next / previous editable one. */
  private onCellEditorTab(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || this.isReadOnly() || !(event.target instanceof Element)) {
      return;
    }
    const cellHost = event.target.closest('kl-grid-form-body-cell');
    if (!cellHost?.querySelector(KlGridForm.editableCellSelector)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const rowIndex = cellHost.getAttribute('data-row-index');
    const binding = cellHost.getAttribute('data-binding');
    const backward = event.shiftKey;
    // The editor's own blur handler commits the value, so `cellChanged` fires once.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // Commit handlers may replace row objects (rows re-render), so find the target after that render.
    setTimeout(() => this.openAdjacentEditableCell(rowIndex, binding, backward), 0);
  }

  /** Enter in an open editor keeps focus on the cell; Enter on a focused closed cell opens the same column below. */
  private onCellEnter(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || this.isReadOnly() || !(event.target instanceof Element)) {
      return;
    }
    const cellHost = event.target.closest('kl-grid-form-body-cell');
    const td = cellHost?.querySelector(KlGridForm.enterNavigableCellSelector);
    const binding = cellHost?.getAttribute('data-binding');
    if (!cellHost || !binding || !(td instanceof HTMLElement)) {
      return;
    }
    const rowIndex = Number(cellHost.getAttribute('data-row-index'));
    if (event.target !== td) {
      // PrimeNG closes the editor on this Enter; commit handlers may re-render the row, so find the cell again.
      setTimeout(() => this.focusClosedCell(rowIndex, binding), 0);
      return;
    }
    // Stops PrimeNG's own Enter handler on the closed cell.
    event.preventDefault();
    event.stopPropagation();
    this.openCellBelow(rowIndex, binding);
  }

  /** A focused closed cell receives the next Enter; skipped while the editor is still open (e.g. invalid value). */
  private focusClosedCell(rowIndex: number, binding: string): void {
    const td = this.findCellHost(rowIndex, binding)?.querySelector(KlGridForm.enterNavigableCellSelector);
    if (td instanceof HTMLElement && !td.contains(document.activeElement)) {
      td.focus();
    }
  }

  private openCellBelow(rowIndex: number, binding: string): void {
    const cellBelow = this.findCellHost(rowIndex + 1, binding);
    if (cellBelow) {
      this.openFirstEditableCell([cellBelow]);
      return;
    }
    const rows = this.orderedViewRows();
    const value = this.getNested(rows[rowIndex], binding);
    // An empty cell on the last row stays put, so repeated Enter does not pile up blank rows.
    const isCellEmpty = value === null || value === undefined || value === '';
    if (rowIndex === rows.length - 1 && !isCellEmpty && this.showAddRow() && !this.interceptAddRow()) {
      const newRow = this.appendRow();
      setTimeout(() => this.openCellInRow(newRow, binding), 0);
    }
  }

  private findCellHost(rowIndex: number, binding: string): Element | undefined {
    return this.bodyCellHosts().find(
      (cell) =>
        cell.getAttribute('data-row-index') === String(rowIndex) && cell.getAttribute('data-binding') === binding
    );
  }

  /** Cell order is DOM order: rows as displayed, columns as {@link displayedDataColumns}. */
  private openAdjacentEditableCell(rowIndex: string | null, binding: string | null, backward: boolean): void {
    const cellHosts = this.bodyCellHosts();
    const currentIndex = cellHosts.findIndex(
      (cell) => cell.getAttribute('data-row-index') === rowIndex && cell.getAttribute('data-binding') === binding
    );
    if (currentIndex < 0) {
      this.closeCellEditor();
      return;
    }
    const candidates = backward ? cellHosts.slice(0, currentIndex).reverse() : cellHosts.slice(currentIndex + 1);
    if (this.openFirstEditableCell(candidates)) {
      return;
    }
    const isLastRow = rowIndex === String(this.orderedViewRows().length - 1);
    if (!backward && isLastRow && this.showAddRow() && !this.interceptAddRow()) {
      const newRow = this.appendRow();
      setTimeout(() => {
        if (!this.openCellInRow(newRow)) {
          this.closeCellEditor();
        }
      }, 0);
      return;
    }
    this.closeCellEditor();
  }

  /** Opens `binding`'s cell in `row`, or its first editable cell when no binding is given; false when none opened. */
  private openCellInRow(row: T, binding?: string): boolean {
    const rowIndex = String(this.orderedViewRows().indexOf(row));
    const rowCells = this.bodyCellHosts().filter(
      (cell) =>
        cell.getAttribute('data-row-index') === rowIndex && (!binding || cell.getAttribute('data-binding') === binding)
    );
    return this.openFirstEditableCell(rowCells);
  }

  /** Clicks the first editable cell in `cellHosts` (PrimeNG opens its editor); false when there is none. */
  private openFirstEditableCell(cellHosts: Element[]): boolean {
    for (const cell of cellHosts) {
      const td = cell.querySelector(KlGridForm.editableCellSelector);
      if (td instanceof HTMLElement) {
        td.click();
        return true;
      }
    }
    return false;
  }

  private bodyCellHosts(): Element[] {
    return Array.from(this.host.nativeElement.querySelectorAll('kl-grid-form-body-cell'));
  }

  /** PrimeNG closes the open cell editor on an outside click. */
  private closeCellEditor(): void {
    document.body.click();
  }

  private canRemoveRow(): boolean {
    return this.rows().length > (this.allowRemoveLastRow() ? 0 : 1);
  }

  removeRow(index: number): void {
    if (!this.canRemoveRow()) {
      return;
    }
    const current = this.rows();
    const removed = current[index];
    const updated = current.filter((_, i) => i !== index);
    this.rows.set(updated);
    this.rowRemoved.emit({ row: removed, index });
  }

  onCellEdit(row: T, col: KlGridFormColumn<T>, rowIndex: number): void {
    this.cellChanged.emit({ row, column: col, rowIndex });
  }

  readonly getNested = (obj: object, path: string): unknown => {
    const keys = path.split('.');
    let current: unknown = obj;
    for (const k of keys) {
      if (current == null || typeof current !== 'object' || current instanceof Date) {
        return undefined;
      }
      current = (current as Record<string, unknown>)[k];
    }
    return current;
  };

  setNested(obj: T, path: string, value: unknown): void {
    setNestedValue(obj, path, value);
  }

  onSelectChange(row: T, col: KlGridFormColumn<T>, value: unknown, rowIndex: number): void {
    this.setNested(row, col.binding, value);
    this.onCellEdit(row, col, rowIndex);
  }

  /**
   * Keeps the row's own value current on every keystroke (so the cell always displays what was
   * typed), but does NOT emit `cellChanged` — consumers that recalculate (and replace row objects)
   * on `cellChanged` would otherwise do so on every keystroke, which kept closing PrimeNG's cell
   * editor mid-typing. See {@link onCellCommit}, fired on Enter/blur instead.
   */
  onNumberChange(row: T, col: KlGridFormColumn<T>, value: unknown): void {
    this.setNested(row, col.binding, value);
    this.pasteAnchor = null;
  }

  /** See {@link onNumberChange}. */
  onCellCommit(row: T, col: KlGridFormColumn<T>, rowIndex: number): void {
    // Enter and the editor's blur can both commit the untouched paste anchor; neither is a new edit.
    if (this.pasteAnchor?.row === row && this.pasteAnchor.binding === col.binding) {
      return;
    }
    this.pasteAnchor = null;
    this.onCellEdit(row, col, rowIndex);
  }

  onDateChange(row: T, col: KlGridFormColumn<T>, value: Date | null, rowIndex: number): void {
    this.setNested(row, col.binding, value);
    this.onCellEdit(row, col, rowIndex);
  }

  onTextChange(row: T, col: KlGridFormColumn<T>, value: string): void {
    this.setNested(row, col.binding, value);
    this.pasteAnchor = null;
  }

  onCheckboxChange(row: T, col: KlGridFormColumn<T>, value: boolean, rowIndex: number): void {
    this.setNested(row, col.binding, value);
    this.rows.set([...this.rows()]);
    this.onCellEdit(row, col, rowIndex);
  }

  /** Clipboard text with more than one cell, pasted into an open cell editor; a single value keeps the editor's own paste. */
  private onCellPaste(event: ClipboardEvent): void {
    if (this.isReadOnly() || !(event.target instanceof Element)) {
      return;
    }
    const cellHost = event.target.closest('kl-grid-form-body-cell');
    const matrix = parsePastedText(event.clipboardData?.getData('text') ?? '');
    const isSingleValue = matrix.length === 0 || (matrix.length === 1 && matrix[0].length === 1);
    if (!cellHost?.parentElement || isSingleValue) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const rowCellHosts = Array.from(cellHost.parentElement.querySelectorAll(':scope > kl-grid-form-body-cell'));
    this.pasteMatrix(matrix, Number(cellHost.getAttribute('data-row-index')), rowCellHosts.indexOf(cellHost));
  }

  /** Clipboard column N goes to the N-th displayed column from the anchor, row N to the N-th on-screen row. */
  private pasteMatrix(matrix: string[][], anchorRowIndex: number, anchorColumnIndex: number): void {
    const targetColumns = this.displayedDataColumns().slice(anchorColumnIndex);
    const existingRows = this.orderedViewRows().slice(anchorRowIndex, anchorRowIndex + matrix.length);
    // Trailing clipboard rows with nothing for a new row add no row; rows in between stay so `pastedRows[i]` is clipboard row i.
    const neededRowCount = matrix.map((cells) => hasPastableCell(targetColumns, cells)).lastIndexOf(true) + 1;
    const missingRowCount = neededRowCount - existingRows.length;
    const canAddRows = this.showAddRow() && !this.interceptAddRow() && missingRowCount > 0;
    // Rows are added (and their `rowAdded` defaults set) before writing, so the pasted values win.
    const newRows = canAddRows ? this.appendRows(missingRowCount) : [];
    const targetRows = [...existingRows, ...newRows];
    const pastedRows: KlGridFormPastedRow[] = targetRows.map((row, offset) => ({
      rowIndex: this.rows().indexOf(row),
      isNew: offset >= existingRows.length,
      written: writePastedCells(row, targetColumns, matrix[offset], this.translate.getCurrentLang() || 'tr'),
    }));
    this.rows.set([...this.rows()]);
    // The anchor editor stays open; its later Enter/blur would send a second `cellChanged` (e.g. a second barcode lookup).
    this.pasteAnchor = { row: targetRows[0], binding: targetColumns[0].binding };
    // Same events as typing each value and pressing Enter, so the parent's per-cell rules run.
    for (const [offset, pastedRow] of pastedRows.entries()) {
      for (const column of targetColumns.filter((col) => pastedRow.written.has(col.binding))) {
        this.onCellEdit(targetRows[offset], column, pastedRow.rowIndex);
      }
    }

    const columnCount = Math.max(...matrix.map((cells) => cells.length));
    const pastedColumns = Array.from({ length: columnCount }, (_, c) => matrix.map((cells) => cells[c] ?? ''));
    const columnsByBinding = new Map<string, string[]>();
    for (const [offset, column] of targetColumns.slice(0, columnCount).entries()) {
      if (!column.isReadOnly) {
        columnsByBinding.set(column.binding, pastedColumns[offset]);
      }
    }
    this.pasteCells.emit({
      binding: targetColumns[0].binding,
      startRowIndex: this.resolveSourceRowIndex(anchorRowIndex),
      values: pastedColumns[0],
      columns: columnCount > 1 ? pastedColumns : undefined,
      columnsByBinding,
      pastedRows,
    });
  }

  readonly pickerDisplayLabel = (row: T, col: KlGridFormColumn<T>): string => {
    const path = col.pickerDisplayPath ?? `${col.binding}.code`;
    const v = this.getNested(row, path);
    if (v == null || v === '') {
      return '';
    }
    return String(v);
  };

  /** True when the picker cell has a non-empty display value (after trim). */
  readonly pickerHasDisplayValue = (row: T, col: KlGridFormColumn<T>): boolean =>
    this.pickerDisplayLabel(row, col).trim().length > 0;

  onPickerButtonClick(filteredRowIndex: number, col: KlGridFormColumn<T>): void {
    const rowIndex = this.resolveSourceRowIndex(filteredRowIndex);
    this.pickerRequest.emit({ rowIndex, column: col });
  }

  clearPickerValue(filteredRowIndex: number, col: KlGridFormColumn<T>): void {
    const rowIndex = this.resolveSourceRowIndex(filteredRowIndex);
    const rows = this.rows();
    const row = rows[rowIndex];
    if (!row) {
      return;
    }
    const empty = col.pickerClearValue !== undefined ? col.pickerClearValue : {};
    this.setNested(row, col.binding, empty);
    const extra = col.pickerClearExtraPatch;
    if (extra) {
      for (const [k, v] of Object.entries(extra)) {
        (row as Record<string, unknown>)[k] = Array.isArray(v) ? [...v] : v;
      }
    }
    this.rows.set([...rows]);
    this.onCellEdit(row, col, rowIndex);
  }

  /** Map displayed row index → index in full `rows()` (for stable callbacks). */
  readonly resolveSourceRowIndex = (filteredRowIndex: number): number => {
    const list = this.orderedViewRows();
    const row = list[filteredRowIndex];
    if (!row) {
      return filteredRowIndex;
    }
    const idx = this.rows().indexOf(row);
    return idx >= 0 ? idx : filteredRowIndex;
  };

  toGridColumn(col: KlGridFormColumn<T>): GridColumn {
    const fmt = col.format;
    const gridFmt: GridColumn['format'] = fmt === 'date' || fmt === 'date-time' || fmt === 'time' ? fmt : undefined;
    const binding = col.type === 'picker' ? (col.pickerDisplayPath ?? `${col.binding}.code`) : col.binding;
    return {
      header: col.header,
      binding,
      format: gridFmt,
    };
  }

  showColumnFilterUi(col: KlGridFormColumn<T>): boolean {
    return (
      this.enableColumnFilter() &&
      col.filterable !== false &&
      col.type !== 'index' &&
      col.type !== 'picker' &&
      col.type !== 'button' &&
      col.type !== 'action-button'
    );
  }

  showColumnSortUi(col: KlGridFormColumn<T>): boolean {
    return (
      this.enableColumnSort() &&
      col.sortable !== false &&
      col.type !== 'index' &&
      col.type !== 'picker' &&
      col.type !== 'button' &&
      col.type !== 'action-button'
    );
  }

  /**
   * PrimeNG `pEditableColumn` + `p-cellEditor` (see Table cell edit). Nested `col.binding`
   * (e.g. `unit.code`) is supported via the same paths as {@link getNested}.
   */
  readonly primeCellEditFocusSelector =
    'input,textarea,select,.p-inputtext,.p-inputnumber-input,.p-datepicker-input,[role=combobox],button,.p-button';

  readonly datePickerFormat = momentFormatToPrimeNg(getUserDateFormat());

  isDateFormatColumn(col: KlGridFormColumn<T>): boolean {
    return col.format === 'date' || col.format === 'date-time';
  }

  readonly resolveDateCellValue = (row: T, col: KlGridFormColumn<T>): Date | null => {
    const raw = this.getNested(row, col.binding);
    if (raw instanceof Date || raw == null || raw === '') {
      return this.parseDateCellValue(raw);
    }

    let rowCache = this.dateCellCache.get(row);
    if (!rowCache) {
      rowCache = new Map();
      this.dateCellCache.set(row, rowCache);
    }

    const cached = rowCache.get(col.binding);
    if (cached && Object.is(cached.raw, raw)) {
      return cached.date;
    }

    const date = this.parseDateCellValue(raw);
    rowCache.set(col.binding, { raw, date });
    return date;
  };

  private parseDateCellValue(raw: unknown): Date | null {
    if (raw == null || raw === '') {
      return null;
    }
    if (raw instanceof Date) {
      return Number.isNaN(raw.getTime()) ? null : raw;
    }
    const d = typeof raw === 'number' ? new Date(raw) : new Date(String(raw));
    return Number.isNaN(d.getTime()) ? null : d;
  }

  /** String shown in read-only cells (text / number / select); not used for checkbox display mode. */
  readonly formatDisplayValue = (row: T, col: KlGridFormColumn<T>, displayRowIndex: number): string => {
    const v = this.exportCellValue(row, col, displayRowIndex);
    if (typeof v === 'boolean') {
      return '';
    }
    if (v === null || v === undefined || v === '') {
      return '';
    }
    if ((col.type === 'number' || col.type === 'decimal') && typeof v === 'number' && !Number.isNaN(v)) {
      const fd = col.displayFractionDigits ?? (col.type === 'decimal' ? col.maxFractionDigits : 0);
      return getNumberFormatter(fd).format(v);
    }
    const text = String(v);
    // Translate özelliği varsa i18n key olarak translate et
    return col.translate ? this.translate.instant(text) : text;
  };

  getColumnFilterStateForCol(col: KlGridFormColumn<T>): ColumnFilterState | null {
    return this.columnFilterStates().get(col.binding) ?? null;
  }

  onColumnFilterApply(state: ColumnFilterState): void {
    const next = new Map(this.columnFilterStates());
    next.set(state.binding, state);
    this.columnFilterStates.set(next);
  }

  onColumnFilterClear(binding: string): void {
    const next = new Map(this.columnFilterStates());
    next.delete(binding);
    this.columnFilterStates.set(next);
  }

  onColumnSortAsc(binding: string): void {
    this.sortBinding.set(binding);
    this.sortDesc.set(false);
  }

  onColumnSortDesc(binding: string): void {
    this.sortBinding.set(binding);
    this.sortDesc.set(true);
  }

  columnReorderActive(): boolean {
    return this.reordering();
  }

  rowReorderActive(): boolean {
    return this.rowReordering() && !this.isReadOnly();
  }

  private resolveColumnMinWidthPx(width: number | string | undefined): number {
    if (width === '*') {
      return KlGridForm.DEFAULT_COLUMN_MIN_WIDTH_PX;
    }
    if (typeof width === 'number' && Number.isFinite(width)) {
      return width;
    }
    if (typeof width === 'string') {
      const trimmed = width.trim();
      if (trimmed.endsWith('px')) {
        const parsed = Number.parseFloat(trimmed.slice(0, -2));
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
      const asNum = Number.parseFloat(trimmed);
      if (Number.isFinite(asNum)) {
        return asNum;
      }
    }
    return KlGridForm.DEFAULT_COLUMN_MIN_WIDTH_PX;
  }

  private buildViewRows(): T[] {
    let data = this.applySearchFilter(this.rows());
    data = this.applyColumnFilters(data);
    data = this.applySort(data);
    return data;
  }

  private applySearchFilter(data: T[]): T[] {
    const q = this.searchQuery().trim().toLowerCase();
    if (!this.effectiveToolbar().search || !q) {
      return data;
    }
    return data.filter((row, rowIndex) => this.rowMatchesSearch(row, q, rowIndex));
  }

  private applyColumnFilters(data: T[]): T[] {
    const states = this.columnFilterStates();
    const active = Array.from(states.values()).filter((f) => f.active);
    if (!active.length) {
      return data;
    }
    return data.filter((row) => active.every((f) => f.checkedValues.has(this.getNested(row, f.binding))));
  }

  private applySort(data: T[]): T[] {
    const binding = this.sortBinding();
    if (!binding || !this.enableColumnSort()) {
      return data;
    }
    const col = this.columns().find((c) => c.binding === binding);
    const desc = this.sortDesc();
    return [...data].sort((a, b) => {
      const cmp = this.compareForSort(a, b, col, binding);
      return desc ? -cmp : cmp;
    });
  }

  private mergeVisibleRowOrderIntoRows(orderedVisible: T[]): T[] {
    const full = [...this.rows()];
    const vis = new Set(orderedVisible);
    let oi = 0;
    return full.map((r) => (vis.has(r) ? orderedVisible[oi++] : r));
  }

  private rowMatchesSearch(row: T, query: string, sourceRowIndex: number): boolean {
    const q = query.toLowerCase();
    return this.columns().some((col) => {
      const text = String(this.exportCellValue(row, col, sourceRowIndex) ?? '').toLowerCase();
      return text.includes(q);
    });
  }

  private compareForSort(a: T, b: T, col: KlGridFormColumn<T> | undefined, binding: string): number {
    const va = this.getNested(a, binding);
    const vb = this.getNested(b, binding);
    if (va == null && vb == null) {
      return 0;
    }
    if (va == null) {
      return -1;
    }
    if (vb == null) {
      return 1;
    }
    if (col?.format === 'date' || col?.format === 'date-time') {
      const da = new Date(va as string | number | Date).getTime();
      const db = new Date(vb as string | number | Date).getTime();
      if (Number.isNaN(da) || Number.isNaN(db)) {
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      }
      return da === db ? 0 : da < db ? -1 : 1;
    }
    if (col?.type === 'number' || col?.type === 'decimal') {
      const na = Number(va);
      const nb = Number(vb);
      if (!Number.isNaN(na) && !Number.isNaN(nb)) {
        return na === nb ? 0 : na < nb ? -1 : 1;
      }
    }
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  }

  /** Excel decimal places of a number/decimal column; `undefined` keeps the cell in General format. */
  private exportFractionDigits(col: KlGridFormColumn<T>): number | undefined {
    if (col.type !== 'number' && col.type !== 'decimal') return undefined;
    return col.displayFractionDigits ?? col.maxFractionDigits;
  }

  private exportCellValue(row: T, col: KlGridFormColumn<T>, rowIndex: number): string | number | boolean {
    if (this.isDateFormatColumn(col)) {
      const v = this.getNested(row, col.binding);
      return formatKlDateValue(v, col.format === 'date-time');
    }
    switch (col.type) {
      case 'button':
      case 'action-button':
        return '';
      case 'picker': {
        const path = col.pickerDisplayPath ?? `${col.binding}.code`;
        const v = this.getNested(row, path);
        return v == null ? '' : String(v);
      }
      case 'index':
        return rowIndex + 1;
      case 'checkbox':
        return !!this.getNested(row, col.binding);
      case 'select': {
        const v = this.getNested(row, col.binding);
        return this.resolveSelectDisplayLabel(row, col, v);
      }
      case 'number':
      case 'decimal': {
        const v = this.getNested(row, col.binding);
        if (v == null || v === '') {
          return '';
        }
        const n = Number(v);
        return Number.isNaN(n) ? String(v) : n;
      }
      default: {
        const v = this.getNested(row, col.binding);
        if (v instanceof Date) {
          return v.toISOString().slice(0, 10);
        }
        const text = v == null ? '' : String(v);
        // Excel export'ta da translate uygula
        return col.translate && text ? this.translate.instant(text) : text;
      }
    }
  }

  private resolveSelectDisplayLabel(row: T, col: KlGridFormColumn<T>, value: unknown): string {
    if (value == null || value === '') {
      return '';
    }

    const labelKey = col.optionLabel ?? 'label';
    const valueKey = col.optionValue ?? 'value';
    const options = typeof col.options === 'function' ? col.options(row) : col.options;

    if (typeof value === 'object') {
      const label = (value as Record<string, unknown>)[labelKey];
      if (label != null) {
        const text = String(label);
        return col.translateOptionLabels ? this.translate.instant(text) : text;
      }
      // Thin `{ id }` reference (no label field of its own): resolve the label from the full option list.
      const id = (value as Record<string, unknown>)['id'];
      const matchById =
        id != null
          ? options?.find((o) => o != null && typeof o === 'object' && (o as Record<string, unknown>)['id'] === id)
          : undefined;
      const matchedLabel = matchById != null ? (matchById as Record<string, unknown>)[labelKey] : null;
      if (matchedLabel != null) {
        const text = String(matchedLabel);
        return col.translateOptionLabels ? this.translate.instant(text) : text;
      }
      return JSON.stringify(value);
    }

    const match = options?.find((option) => {
      if (option == null || typeof option !== 'object') {
        return false;
      }
      return (option as Record<string, unknown>)[valueKey] === value;
    });

    if (match != null && typeof match === 'object') {
      const label = (match as Record<string, unknown>)[labelKey];
      if (label != null) {
        const text = String(label);
        return col.translateOptionLabels ? this.translate.instant(text) : text;
      }
    }

    return String(value);
  }

  onCustomToolbarAction(action: ActionDto): void {
    const sel = this.selectedRowIndex();
    const selectedRow = sel != null ? (this.rows()[sel] ?? null) : null;
    const targetRow = selectedRow ?? this.orderedViewRows()[0] ?? null;
    if (action.method) {
      const ctx: GridActionContext = { item: targetRow };
      action.method(ctx);
    } else {
      this.customToolbarAction.emit({ action, row: targetRow });
    }
  }

  openColumnSettings(): void {
    this.sidebarVisible.set(true);
  }

  onColumnDrawerResetToDefault(): void {
    this.effectiveColumns.set(this.columns().map((c) => ({ ...c })));
    this.columnFilterStates.set(new Map());
    this.sortBinding.set(null);
    this.sortDesc.set(false);
    queueMicrotask(() => this.columnSettingsDrawer()?.syncAfterParentReset());
  }

  onColumnsChanged(updated: GridColumn[]): void {
    this.effectiveColumns.set(this.applyGridColumns(updated));
  }

  readonly effectiveColumnsForDrawer = computed<GridColumn[]>(() =>
    this.klGridFormColumnsToGridColumns(this.effectiveColumns())
  );

  private loadDefaultGridDesign(parent: string, module: string, key: string, companyId: number): void {
    this.api
      .getFilteredData<Record<string, unknown>>('design', {
        parent: { module: parent },
        product_type: 'JRETAIL',
        module,
        key,
        type: 'GRID_DESIGN',
        company_id: companyId,
      })
      .subscribe({
        next: (res) => {
          const rows = (res?.data as Record<string, unknown>[] | undefined) ?? [];
          // The response can also hold other grids' designs and the module's fast-filter record.
          const designRows = rows.filter(
            (row) => row['module'] === module && row['key'] === key && (!row['type'] || row['type'] === 'GRID_DESIGN')
          );
          // Last DEFAULT in the list wins; ids may be Mongo strings, so list order decides, not id order.
          const def = KlGridForm.pickDefaultDesign(designRows);
          if (!def) {
            return;
          }
          try {
            const content = JSON.parse(String(def['content'] ?? '{}')) as { columns?: GridColumn[] };
            if (content.columns?.length) {
              // Form grid design content stores `visible` inverted (`true` = hidden).
              const columns = content.columns.map((column) => ({ ...column, visible: column.visible !== true }));
              this.onColumnsChanged(columns);
            }
          } catch {
            /* ignore malformed design */
          }
        },
        error: () => {},
      });
  }

  /** Last DEFAULT in the response list wins; ids may be Mongo strings, so list order decides. */
  private static pickDefaultDesign(rows: Record<string, unknown>[]): Record<string, unknown> | undefined {
    let best: Record<string, unknown> | undefined;
    for (const r of rows) {
      if (r['level_default'] === 'DEFAULT') {
        best = r;
      }
    }
    return best;
  }

  private applyGridColumns(updated: GridColumn[]): KlGridFormColumn<T>[] {
    const base = this.columns();
    const baseMap = new Map(base.map((c) => [c.binding, c]));
    const result: KlGridFormColumn<T>[] = [];
    for (const u of updated) {
      const binding = u.binding ?? '';
      const b = baseMap.get(binding);
      if (b) {
        result.push({
          ...b,
          visible: u.visible !== false,
          width: u.width ?? b.width,
        });
      }
    }
    // Insert columns missing from `updated` next to their `base` neighbor, not at the end.
    base.forEach((b, i) => {
      if (result.some((r) => r.binding === b.binding)) {
        return;
      }
      let insertAt = -1;
      for (const preceding of base.slice(0, i).reverse()) {
        const precedingIndex = result.findIndex((r) => r.binding === preceding.binding);
        if (precedingIndex !== -1) {
          insertAt = precedingIndex + 1;
          break;
        }
      }
      if (insertAt === -1) {
        for (const succeeding of base.slice(i + 1)) {
          const succeedingIndex = result.findIndex((r) => r.binding === succeeding.binding);
          if (succeedingIndex !== -1) {
            insertAt = succeedingIndex;
            break;
          }
        }
      }
      result.splice(insertAt === -1 ? result.length : insertAt, 0, { ...b });
    });
    return result;
  }

  /** Keep design visibility / width / order; refresh defs (options, isReadOnly) from parent. */
  private mergeColumnDefsPreservingDesign(
    incoming: KlGridFormColumn<T>[],
    previous: KlGridFormColumn<T>[]
  ): KlGridFormColumn<T>[] {
    const incomingMap = new Map(incoming.map((c) => [c.binding, c]));
    const prevBindings = new Set(previous.map((c) => c.binding));
    const result: KlGridFormColumn<T>[] = [];
    for (const p of previous) {
      const next = incomingMap.get(p.binding);
      if (!next) continue;
      result.push({
        ...next,
        visible: p.visible,
        width: p.width ?? next.width,
      });
    }
    for (const c of incoming) {
      if (!prevBindings.has(c.binding)) {
        result.push({ ...c });
      }
    }
    return result;
  }

  private klGridFormColumnsToGridColumns(cols: KlGridFormColumn<T>[]): GridColumn[] {
    return cols.map((c) => ({
      header: c.header,
      binding: c.binding,
      width: c.width,
      visible: c.visible !== false,
    }));
  }
}
